import {
  Box,
  Divider,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material";
import ButtonBase from "@mui/material/ButtonBase";
import Add from "@mui/icons-material/Add";
import ChevronRight from "@mui/icons-material/ChevronRight";
import Delete from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import React from "react";
import { PropertyViewTypeOptionsSelect, SelectOption } from "./propertyTypeOptions";
import { useDispatch } from "react-redux";
import { useAppId } from "../../hook";
import { useSelectedNotebookPageIdMap } from "../../hook/page";
import { useFetchPageProps, useSetPageProp, useDeletePageProp } from "../../hook/page/pageProp";
import { useMenu } from "../../hook/menu";
import { DynamicTextField } from "../dynamicTextField";
import { setPageProp as setPagePropApi, deletePageProp as deletePagePropApi, listPageProps as listPagePropsApi } from "../../api/page/pageProp";
import { ActionType } from "../../actionTypes";
import { AppDispatch } from "../../reducer";
import { PagePropTypeText, PagePropTypeSelect } from "../../types/page/pageProp";
import { PropertyTypeDisplayName, PropertyTypeKey } from "../../types/page/property";

const PROP_TYPE_OPTIONS = [
  { key: PagePropTypeText,   label: PropertyTypeDisplayName[PropertyTypeKey.TEXT] },
  { key: PagePropTypeSelect, label: PropertyTypeDisplayName[PropertyTypeKey.SELECT] },
];

const PagePropEditMenu: React.FC<{
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  name: string;
  value: string;
  propType: string;
  propOptions: SelectOption[];
  pageId: string;
}> = ({ anchorEl, open, onClose, name, value, propType, propOptions, pageId }) => {
  const [editName, setEditName] = React.useState(name);
  const [editType, setEditType] = React.useState<string>(propType);
  const [editOptions, setEditOptions] = React.useState<SelectOption[]>(propOptions ?? []);
  const [appId] = useAppId();
  const dispatch = useDispatch<AppDispatch>();
  const deleteProp = useDeletePageProp(pageId);

  React.useEffect(() => { setEditName(name); }, [name]);
  React.useEffect(() => { setEditType(propType); }, [propType]);
  React.useEffect(() => { setEditOptions(propOptions ?? []); }, [JSON.stringify(propOptions)]);

  const handleSave = async () => {
    if (!appId) { onClose(); return; }
    const nameChanged = editName.trim() !== "" && editName !== name;
    const typeChanged = editType !== propType;
    const optionsChanged = JSON.stringify(editOptions) !== JSON.stringify(propOptions);
    if (!nameChanged && !typeChanged && !optionsChanged) { onClose(); return; }
    const targetName = nameChanged ? editName.trim() : name;
    await setPagePropApi(appId, pageId, targetName, value, editType, editOptions);
    if (nameChanged) {
      const updated = await deletePagePropApi(appId, pageId, name);
      dispatch({ type: ActionType.SET_PAGE_PROPS, pageId, props: updated });
    } else {
      const updated = await listPagePropsApi(appId, pageId);
      dispatch({ type: ActionType.SET_PAGE_PROPS, pageId, props: updated });
    }
    onClose();
  };

  const handleDelete = () => { deleteProp(name); onClose(); };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: "top", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <MenuItem disabled sx={{ width: "15rem" }}>
        <ListItemText>Property</ListItemText>
      </MenuItem>
      <Box sx={{ padding: "1rem", width: "15rem" }}>
        <TextField
          label="name"
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          onKeyDown={(e) => e.stopPropagation()}
          size="small"
          fullWidth
        />
      </Box>
      <Box sx={{ px: "1rem", pb: "0.5rem", width: "15rem" }}>
        <InputLabel id="edit-prop-type-label" sx={{ fontSize: "0.75rem" }}>Type</InputLabel>
        <Select
          labelId="edit-prop-type-label"
          value={editType}
          onChange={(e) => setEditType(e.target.value)}
          size="small"
          fullWidth
        >
          {PROP_TYPE_OPTIONS.map((opt) => (
            <MenuItem key={opt.key} value={opt.key}>{opt.label}</MenuItem>
          ))}
        </Select>
      </Box>
      {editType === PagePropTypeSelect && (
        <PropertyViewTypeOptionsSelect options={editOptions} setOptions={setEditOptions} />
      )}
      <MenuItem onClick={handleSave}>
        <ListItemIcon><SaveIcon color="primary" fontSize="small" /></ListItemIcon>
        <ListItemText>Save</ListItemText>
      </MenuItem>
      <Divider />
      <MenuItem onClick={handleDelete} aria-label={`delete-prop-${name}`}>
        <ListItemIcon><Delete color="primary" fontSize="small" /></ListItemIcon>
        <ListItemText>Delete</ListItemText>
      </MenuItem>
    </Menu>
  );
};

const PagePropertyRow: React.FC<{
  name: string;
  value: string;
  propType: string;
  propOptions: SelectOption[];
  pageId: string;
}> = ({ name, value, propType, propOptions, pageId }) => {
  const setProp = useSetPageProp(pageId);
  const [localVal, setLocalVal] = React.useState<string>(value);
  const prevValueRef = React.useRef<string>(value);
  const { anchorEl, open, handleOpen, onClose } = useMenu();
  const theme = useTheme();
  const [selectMenuAnchor, setSelectMenuAnchor] = React.useState<HTMLElement | null>(null);

  React.useEffect(() => {
    if (value !== prevValueRef.current) {
      prevValueRef.current = value;
      setLocalVal(value);
    }
  }, [value]);

  const onChange = (v: string) => {
    setLocalVal(v);
    prevValueRef.current = v;
    setProp(name, v, propType, propOptions);
  };

  if (propType === PagePropTypeSelect) {
    return (
      <Box
        sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}
        data-testid={`page-prop-row-${name}`}
      >
        <PagePropEditMenu
          anchorEl={anchorEl}
          open={open}
          onClose={onClose}
          name={name}
          value={localVal}
          propType={propType}
          propOptions={propOptions}
          pageId={pageId}
        />
        <Menu open={Boolean(selectMenuAnchor)} anchorEl={selectMenuAnchor}
              onClose={() => setSelectMenuAnchor(null)}>
          <MenuItem disabled sx={{ width: "15rem" }}>
            <ListItemText>Select Value</ListItemText>
          </MenuItem>
          <Box sx={{ padding: "1rem", width: "15rem" }}>
            <Select open={Boolean(selectMenuAnchor)} value={localVal}
              onClose={() => setSelectMenuAnchor(null)}
              onChange={(e) => { onChange(e.target.value as string); setSelectMenuAnchor(null); }}
              sx={{ width: "100%" }}>
              {(propOptions ?? []).map((opt) => (
                <MenuItem key={opt.label} value={opt.label}>{opt.label}</MenuItem>
              ))}
            </Select>
          </Box>
        </Menu>
        <Box sx={{ width: "15rem" }}>{name}</Box>
        <Box sx={{ display: "flex", flexGrow: 1 }}>
          <ButtonBase onClick={(e) => setSelectMenuAnchor(e.currentTarget)}
            data-testid="select-value-btn"
            sx={{ "&:hover": { backgroundColor: theme.palette.action.hover },
                  padding: "1rem", width: "100%", justifyContent: "left" }}>
            <Typography sx={{ color: localVal ? "inherit" : theme.palette.text.disabled }}>
              {localVal || "No Value"}
            </Typography>
          </ButtonBase>
        </Box>
        <IconButton size="small" aria-label={`edit-prop-${name}`} onClick={handleOpen}
          sx={{ ml: 0.5, opacity: 0.4, "&:hover": { opacity: 1 } }}>
          <ChevronRight fontSize="inherit" />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box
      sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}
      data-testid={`page-prop-row-${name}`}
    >
      <PagePropEditMenu
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        name={name}
        value={localVal}
        propType={propType}
        propOptions={propOptions}
        pageId={pageId}
      />
      <Box sx={{ width: "15rem" }}>{name}</Box>
      <Box sx={{ display: "flex", flexGrow: 1 }}>
        <DynamicTextField text={localVal} setText={onChange} />
      </Box>
      <IconButton
        size="small"
        aria-label={`edit-prop-${name}`}
        onClick={handleOpen}
        sx={{ ml: 0.5, opacity: 0.4, "&:hover": { opacity: 1 } }}
      >
        <ChevronRight fontSize="inherit" />
      </IconButton>
    </Box>
  );
};

const AddPagePropertyMenu: React.FC<{
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  pageId: string;
}> = ({ anchorEl, open, onClose, pageId }) => {
  const setProp = useSetPageProp(pageId);
  const [name, setName] = React.useState("");
  const [value, setValue] = React.useState("");
  const [propType, setPropType] = React.useState<string>(PagePropTypeText);
  const [addOptions, setAddOptions] = React.useState<SelectOption[]>([]);

  const submit = () => {
    if (!name.trim()) return;
    setProp(name.trim(), value, propType, addOptions);
    setName(""); setValue(""); setPropType(PagePropTypeText); setAddOptions([]); onClose();
  };

  const cancel = () => { setName(""); setValue(""); setPropType(PagePropTypeText); setAddOptions([]); onClose(); };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={cancel}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
    >
      <MenuItem disabled sx={{ width: "15rem" }}>
        <ListItemText>New Property</ListItemText>
      </MenuItem>
      <Box sx={{ padding: "1rem", width: "15rem", display: "flex", flexDirection: "column", gap: 1 }}>
        <TextField
          label="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.stopPropagation()}
          inputProps={{ "aria-label": "new-prop-name" }}
          size="small"
          fullWidth
          autoFocus
        />
        <TextField
          label="value"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Enter") submit(); }}
          inputProps={{ "aria-label": "new-prop-value" }}
          size="small"
          fullWidth
        />
      </Box>
      <Box sx={{ px: "1rem", pb: "0.5rem", width: "15rem" }}>
        <InputLabel id="add-prop-type-label" sx={{ fontSize: "0.75rem" }}>Type</InputLabel>
        <Select
          labelId="add-prop-type-label"
          value={propType}
          onChange={(e) => setPropType(e.target.value)}
          size="small"
          fullWidth
          inputProps={{ "aria-label": "new-prop-type" }}
        >
          {PROP_TYPE_OPTIONS.map((opt) => (
            <MenuItem key={opt.key} value={opt.key}>{opt.label}</MenuItem>
          ))}
        </Select>
      </Box>
      {propType === PagePropTypeSelect && (
        <PropertyViewTypeOptionsSelect options={addOptions} setOptions={setAddOptions} />
      )}
      <MenuItem onClick={submit} aria-label="confirm-add-prop">
        <ListItemIcon><Add color="primary" fontSize="small" /></ListItemIcon>
        <ListItemText>Add</ListItemText>
      </MenuItem>
      <MenuItem onClick={cancel} aria-label="cancel-add-prop">
        <ListItemText>Cancel</ListItemText>
      </MenuItem>
    </Menu>
  );
};

export const PageProperties: React.FC = () => {
  const { selectedNotebookPageId: pageId } = useSelectedNotebookPageIdMap();
  const props = useFetchPageProps(pageId);
  const { anchorEl, open, handleOpen, onClose } = useMenu();
  const hasProps = pageId && Object.keys(props).length > 0;

  if (!pageId) return <></>;

  return (
    <>
      {hasProps && <Divider sx={{ mx: 2, my: 0.5 }} />}
      {hasProps && Object.entries(props).sort(([a], [b]) => a.localeCompare(b)).map(([name, data]) => (
        <PagePropertyRow key={name} name={name} value={data.value} propType={data["prop-type"]} propOptions={data.options ?? []} pageId={pageId} />
      ))}
      <Box sx={{ px: 2, py: 0.5 }}>
        <AddPagePropertyMenu
          anchorEl={anchorEl}
          open={open}
          onClose={onClose}
          pageId={pageId}
        />
        <Tooltip title="Add page property" placement="right">
          <IconButton
            size="small"
            aria-label="add-page-property"
            onClick={handleOpen}
          >
            <Add fontSize="inherit" />
          </IconButton>
        </Tooltip>
      </Box>
    </>
  );
};
